import React, { useState, useEffect, Suspense } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Header from './Header';

const StreamerRoute = () => {
  const { streamer, page } = useParams();
  const [StreamerComponent, setStreamerComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const checkStreamerStatusAndLoad = async () => {
      try {
        setLoading(true);
        setError(false);
        
        // Block submit and gallery pages - redirect to global routes instead
        if (page === 'submit' || page === 'gallery') {
          console.log(`Redirecting ${streamer}/${page} to global /${page} route`);
          window.location.href = `/${page}`;
          return;
        }
        
        // First, check streamer status via API (for ALL pages - main and sub-pages)
        console.log(`Checking streamer status for: ${streamer}${page ? `/${page}` : ''}`);
        
        try {
          const response = await fetch(`/.netlify/functions/get-streamer-data?streamer=${encodeURIComponent(streamer)}`);
          const result = await response.json();
          
          if (response.ok && result.success) {
            // Check if streamer status is pending
            if (result.data.status === 'pending') {
              console.log(`Streamer ${streamer} has pending status, blocking access to ${page ? `sub-page /${page}` : 'main page'}`);
              const pendingComponent = await import(`../pages/streamers/Pending.js`);
              setStreamerComponent(() => pendingComponent.default);
              return;
            }
            // If approved or other status, continue with normal loading
            console.log(`Streamer ${streamer} has ${result.data.status} status, allowing access to ${page ? `/${page}` : 'main page'}`);
          } else {
            // If API call fails or streamer not found, redirect to home
            console.warn(`Streamer ${streamer} not found, redirecting to home`);
            setError(true);
            return;
          }
        } catch (apiError) {
          console.error(`Failed to check streamer status:`, apiError);
          // If API check fails, redirect to home for security
          console.warn(`Cannot verify streamer status, redirecting to home for security`);
          setError(true);
          return;
        }
        
        let component;
        
        // Use dynamic imports from the single streamers folder
        if (!page || page === '') {
          // Load the main index page
          console.log(`Loading streamer home for: ${streamer}`);
          try {
            component = await import(`../pages/streamers/index.js`);
          } catch (dynamicError) {
            console.warn(`Dynamic import failed for streamer index, redirecting to home`);
            setError(true);
            return;
          }
        } else {
          // Load specific page components
          console.log(`Loading streamer page: ${streamer}/${page}`);
          // Convert kebab-case to PascalCase (e.g., "top-voters" -> "TopVoters")
          const capitalizedPage = page
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
          
          try {
            component = await import(`../pages/streamers/${capitalizedPage}.js`);
          } catch (dynamicError) {
            console.warn(`Dynamic import failed for ${streamer}/${page}, redirecting to home`);
            setError(true);
            return;
          }
        }
        
        setStreamerComponent(() => component.default);
      } catch (err) {
        console.error(`Failed to load streamer component for: ${streamer}/${page || 'index'}`, err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (streamer) {
      checkStreamerStatusAndLoad();
    } else {
      setLoading(false);
      setError(true);
    }
  }, [streamer, page]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header streamer={streamer} />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Loading {streamer}{page ? `/${page}` : ''}...
            </h2>
            <p className="text-gray-400">Please wait while we load the page</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state - redirect to home if streamer doesn't exist
  if (error || !StreamerComponent) {
    return <Navigate to="/" replace />;
  }

  // Render the streamer component with header and pass streamer as prop
  return (
    <div className="min-h-screen bg-gray-900">
      <Header streamer={streamer} />
      <main className="pt-20">
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        }>
          <StreamerComponent streamer={streamer} />
        </Suspense>
      </main>
    </div>
  );
};

export default StreamerRoute; 